<template>
  <div class="dw-portal">
    <section
      v-if="active"
      ref="window"
      class="dw"
      :class="{
        'dw--narrow': isNarrow && !minimized,
        'dw--maximized': maximized && !isNarrow && !minimized,
        'dw--minimized': minimized,
        'dw--interacting': !!interaction,
      }"
      :style="windowStyle"
      role="dialog"
      aria-modal="false"
      :aria-labelledby="titleId"
      tabindex="-1"
      @keydown.esc="onEscape">
      <div
        v-if="minimized"
        class="dw__mini"
        title="Drag to move · double-click to restore"
        @pointerdown="startMiniMove"
        @dblclick="onMiniDoubleClick">
        <span class="dw__mini-icon" :class="`fiv-viv fiv-icon-${extension || 'blank'}`" aria-hidden="true"></span>
        <span class="dw__mini-title" :title="title">{{ title }}</span>
        <v-chip v-if="shownVersionNumber" x-small outlined class="dw__mini-chip">v{{ shownVersionNumber }}</v-chip>
        <v-btn icon x-small aria-label="Restore the document window" title="Restore" @click="restore">
          <v-icon small>mdi-window-restore</v-icon>
        </v-btn>
        <v-btn icon x-small aria-label="Close" title="Close" @click="close">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>

      <header
        v-show="!minimized"
        class="dw__header"
        :class="{ 'dw__header--movable': movable }"
        :title="movable ? 'Drag to move · double-click to maximize' : null"
        @pointerdown="startMove"
        @dblclick="onHeaderDoubleClick">
        <span class="dw__file-icon" :class="`fiv-viv fiv-icon-${extension || 'blank'}`" aria-hidden="true"></span>
        <div class="dw__titles">
          <h2 :id="titleId" class="dw__title" :title="title">{{ title }}</h2>
          <div v-if="subtitle" class="dw__subtitle" :title="subtitle">{{ subtitle }}</div>
        </div>
        <div v-if="document" class="dw__chips">
          <v-chip v-if="shown.revisionNumber" x-small outlined color="primary">Rev {{ shown.revisionNumber }}</v-chip>
          <v-chip v-if="shownVersionNumber" x-small :color="isOldVersion ? undefined : 'primary'" :outlined="isOldVersion">
            v{{ shownVersionNumber }}
            <v-icon v-if="!isOldVersion && document.isCurrentOverridden" x-small right color="warning">mdi-pin</v-icon>
          </v-chip>
          <v-chip v-if="documentTypeName" x-small>{{ documentTypeName }}</v-chip>
          <v-chip x-small :color="document.facilityGroupId ? 'warning' : 'info'" outlined>
            <v-icon x-small left>{{ document.facilityGroupId ? 'mdi-account-group' : 'mdi-domain' }}</v-icon>
            {{ document.facilityGroupId ? 'Group' : 'Facility' }}
          </v-chip>
        </div>
        <v-spacer></v-spacer>
        <div class="dw__actions">
          <v-btn v-if="canEdit" small outlined color="primary" :disabled="isOldVersion" @click="edit">
            <v-icon small left>mdi-pencil-outline</v-icon>
            Edit
          </v-btn>
          <v-btn
            icon
            small
            :input-value="panelOpen"
            :aria-label="panelOpen ? 'Hide document information' : 'Show document information'"
            :title="panelOpen ? 'Hide information' : 'Show information'"
            @click="togglePanel">
            <v-icon small>mdi-dock-right</v-icon>
          </v-btn>
          <v-btn icon small aria-label="Minimize to a bar" title="Minimize" @click="minimize">
            <v-icon small>mdi-window-minimize</v-icon>
          </v-btn>
          <v-btn
            v-if="!isNarrow"
            icon
            small
            :aria-label="maximized ? 'Restore window size' : 'Maximize window'"
            :title="maximized ? 'Restore' : 'Maximize'"
            @click="toggleMaximized">
            <v-icon small>{{ maximized ? 'mdi-window-restore' : 'mdi-window-maximize' }}</v-icon>
          </v-btn>
          <v-btn icon small aria-label="Close" title="Close" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </header>

      <div v-if="isOldVersion" v-show="!minimized" class="dw__banner" role="status">
        <v-icon small color="warning">mdi-history</v-icon>
        <span>
          You are viewing v{{ selectedVersion.versionNumber }}<span v-if="selectedVersion.revisionNumber"> (Rev {{ selectedVersion.revisionNumber }})</span>.
          <span v-if="document.currentVersionNumber">The current version is v{{ document.currentVersionNumber }}.</span>
        </span>
        <v-btn x-small outlined color="warning" @click="showCurrent">Show current</v-btn>
        <v-btn v-if="canManage" x-small text color="warning" @click="setSelectedAsCurrent">Set as current</v-btn>
      </div>

      <!-- v-show, not v-if: the viewer stays loaded while minimized, so restoring keeps page and zoom. -->
      <div v-show="!minimized" ref="body" class="dw__body">
        <section class="dw__viewer" aria-label="Document">
          <div v-if="loadError" class="dw__state" role="alert">
            <v-icon large class="dw__state-icon">mdi-file-alert-outline</v-icon>
            <p>{{ loadError }}</p>
            <v-btn small outlined color="primary" @click="loadDocument">Try again</v-btn>
          </div>
          <div v-else-if="!viewerDocument" class="dw__state" aria-busy="true">
            <v-progress-circular indeterminate size="40" width="3" color="primary" />
          </div>
          <DocumentViewerShell v-else :document="viewerDocument" :toolbar="compactToolbar ? 'compact' : 'full'" />
        </section>

        <aside v-if="document" class="dw__panel" :class="{ 'dw__panel--open': panelOpen }" aria-label="Document information">
          <div
            v-if="panelOpen && !isNarrow"
            class="dw__panel-resize"
            role="separator"
            aria-orientation="vertical"
            :aria-valuenow="effectivePanelWidth"
            tabindex="0"
            aria-label="Resize the information panel"
            title="Drag to resize"
            @pointerdown="startPanelResize"
            @keydown="resizePanelWithKeyboard"></div>

          <div v-show="panelOpen" class="dw__panel-content" :style="panelContentStyle">
            <div class="dw__panel-header">
              <h3 class="dw__panel-title">
                {{ currentTab.label }}<span v-if="currentTab.key === 'versions' && versionTotal" class="dw__tab-count">{{ versionTotal }}</span>
              </h3>
              <v-btn icon x-small aria-label="Collapse the information panel" title="Collapse" @click="panelOpen = false">
                <v-icon small>mdi-chevron-double-right</v-icon>
              </v-btn>
            </div>
            <div class="dw__panel-body">
              <WorkspaceDetailsTab
                v-show="tabKey === 'details'"
                :document="document"
                :shown="isOldVersion ? viewerDocument : null"
                :document-type-name="documentTypeName" />
              <WorkspaceVersionsTab
                v-if="isAttached"
                v-show="tabKey === 'versions'"
                ref="versions"
                :document-id="documentId"
                :document="document"
                :selected-id="shownRevisionId"
                :can-manage="canManage"
                :can-upload="canUpload"
                @select="selectVersion"
                @loaded="onVersionsLoaded"
                @load-failed="onVersionsLoaded({ versions: [], versionNumbers })"
                @document-changed="onDocumentChanged"
                @reload="loadDocument" />
              <WorkspaceHistoryTab
                v-if="isAttached"
                v-show="tabKey === 'history'"
                ref="history"
                :document-id="documentId"
                :version-numbers="versionNumbers"
                :active="panelOpen && tabKey === 'history'" />
              <WorkspaceLinksTab v-if="tabKey === 'links'" :document="document" />
            </div>
          </div>

          <nav class="dw__rail" aria-label="Document information">
            <button
              v-for="t in tabs"
              :key="t.key"
              type="button"
              class="dw__rail-button"
              :class="{ 'dw__rail-button--active': panelOpen && tabKey === t.key }"
              :aria-label="t.label"
              :aria-pressed="String(panelOpen && tabKey === t.key)"
              :title="t.label"
              @click="toggleTab(t.key)">
              <v-icon small>{{ t.icon }}</v-icon>
              <span v-if="t.key === 'versions' && versionTotal" class="dw__rail-badge">{{ versionTotal }}</span>
            </button>
          </nav>
        </aside>
      </div>

      <template v-if="resizable">
        <div
          v-for="direction in RESIZE_DIRECTIONS"
          :key="direction"
          class="dw__resize"
          :class="`dw__resize--${direction}`"
          aria-hidden="true"
          @pointerdown="startResize($event, direction)"></div>
        <button
          type="button"
          class="dw__resize-grip"
          aria-label="Resize the window. Use the arrow keys."
          title="Drag or use the arrow keys to resize"
          @pointerdown="startResize($event, 'se')"
          @keydown="resizeWithKeyboard"></button>
      </template>
    </section>
    <UpdateDocumentModal ref="updateDocumentModal" @updateCompleted="onUpdateCompleted" />
  </div>
</template>

<script>
  import DocumentViewerShell from '../viewer/DocumentViewerShell.vue';
  import WorkspaceDetailsTab from './workspace/WorkspaceDetailsTab.vue';
  import WorkspaceVersionsTab from './workspace/WorkspaceVersionsTab.vue';
  import WorkspaceHistoryTab from './workspace/WorkspaceHistoryTab.vue';
  import WorkspaceLinksTab from './workspace/WorkspaceLinksTab.vue';
  import UpdateDocumentModal from '@/components/attachments/update-document-modal.vue';
  import documentsService from '@Task/service/documents.service';
  import permissions from '@Share/config/permissions';
  import { $has, $handleError } from '@/utils';
  import { documentExtension } from '../viewer/documentKinds';
  import { centeredRect, clampRect, moveRect, resizeRect, resizePanelWidth } from '../utils/windowGeometry';
  import { EventBus, DOCUMENT_EVENTS } from '@/event-bus';
  import { warn } from '@/utils/debugLogger';

  const TABS = Object.freeze([
    { key: 'details', label: 'Details', icon: 'mdi-information-outline', attachedOnly: false },
    { key: 'versions', label: 'Versions', icon: 'mdi-history', attachedOnly: true },
    { key: 'history', label: 'History', icon: 'mdi-timeline-clock-outline', attachedOnly: true },
    { key: 'links', label: 'Links', icon: 'mdi-tag-multiple-outline', attachedOnly: true },
  ]);
  const RESIZE_DIRECTIONS = Object.freeze(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']);
  const WINDOW_STORAGE_KEY = 'documentWorkspace.window';
  const PANEL_WIDTH_STORAGE_KEY = 'documentWorkspace.panelWidth';
  const MINI_STORAGE_KEY = 'documentWorkspace.miniBar';
  const MINI_SIZE = Object.freeze({ width: 340, height: 44 });
  const MINI_BOTTOM_GAP = 16;
  const MIN_WINDOW = Object.freeze({ width: 520, height: 380 });
  const DEFAULT_PANEL_WIDTH = 360;
  const MIN_PANEL_WIDTH = 240;
  const MIN_VIEWER_WIDTH = 280;
  const RAIL_WIDTH = 44;
  const KEYBOARD_STEP = 24;
  const KEYBOARD_LARGE_STEP = 64;
  /** Below this window width the viewer toolbar folds its secondary actions into a menu. */
  const COMPACT_TOOLBAR_WIDTH = 900;

  function readStored(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key));
    } catch (err) {
      return null;
    }
  }

  function writeStored(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      // Storage can be unavailable (private mode); the window still works, it just forgets.
    }
  }

  function viewportBounds() {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  function isValidRect(rect) {
    return !!rect && ['left', 'top', 'width', 'height'].every((key) => Number.isFinite(rect[key]));
  }

  /**
   * Document Workspace: a floating window with the document, and a collapsible side panel with its
   * details, versions, history and links. It does not block the page, so the model stays usable
   * behind it. Drag it by the header, resize it from any edge, maximize it with a double-click;
   * its size, position and panel width are remembered.
   *
   * Open with `open({ documentId?, document?, revisionId?, tab?, readonly? })`. `documentId` loads
   * the document; a `document` without an id shows just that file and its details. Without `tab`
   * the side panel starts collapsed; with one it opens on that tab. Emits `changed` with the new
   * DocumentVM after an edit, a new version, a pin or a revert (and broadcasts
   * DOCUMENT_EVENTS.DOCUMENT_CHANGED).
   *
   * The element is moved to the app root (`[data-app]`) when mounted, so `position: fixed` is not
   * trapped by a transformed ancestor and Vuetify menus inside it keep their theme.
   */
  export default {
    name: 'DocumentWorkspaceDialog',
    components: {
      DocumentViewerShell,
      WorkspaceDetailsTab,
      WorkspaceVersionsTab,
      WorkspaceHistoryTab,
      WorkspaceLinksTab,
      UpdateDocumentModal,
    },
    data() {
      return {
        RESIZE_DIRECTIONS,
        active: false,
        documentId: null,
        document: null,
        readonly: false,
        loadError: '',
        selectedVersion: null,
        pendingRevisionId: null,
        versionNumbers: {},
        versionTotal: 0,
        tabKey: 'details',
        panelOpen: false,
        panelWidth: Number(readStored(PANEL_WIDTH_STORAGE_KEY)) || DEFAULT_PANEL_WIDTH,
        rect: null,
        maximized: false,
        minimized: false,
        miniPosition: null,
        interaction: null,
      };
    },
    computed: {
      titleId() {
        return `document-workspace-title-${this._uid}`;
      },
      isNarrow() {
        return this.$vuetify.breakpoint.smAndDown;
      },
      movable() {
        return !this.isNarrow && !this.maximized && !this.minimized;
      },
      miniRect() {
        const width = Math.min(MINI_SIZE.width, window.innerWidth - 16);
        const position = this.miniPosition || this.defaultMiniPosition(width);
        return { left: position.left, top: position.top, width, height: MINI_SIZE.height };
      },
      resizable() {
        return this.movable;
      },
      compactToolbar() {
        if (this.isNarrow) return true;
        if (this.maximized || !this.rect) return false;
        const panel = this.panelOpen ? this.effectivePanelWidth : 0;
        return this.rect.width - panel < COMPACT_TOOLBAR_WIDTH;
      },
      windowStyle() {
        if (this.minimized) {
          const mini = this.miniRect;
          return { left: `${mini.left}px`, top: `${mini.top}px`, width: `${mini.width}px`, height: `${mini.height}px` };
        }
        if (this.isNarrow || this.maximized || !this.rect) return null;
        return {
          left: `${this.rect.left}px`,
          top: `${this.rect.top}px`,
          width: `${this.rect.width}px`,
          height: `${this.rect.height}px`,
        };
      },
      /** The remembered panel width, capped so the document keeps room in a small window. */
      effectivePanelWidth() {
        const windowWidth = this.maximized || !this.rect ? window.innerWidth : this.rect.width;
        return Math.max(MIN_PANEL_WIDTH, Math.min(this.panelWidth, windowWidth - RAIL_WIDTH - MIN_VIEWER_WIDTH));
      },
      panelContentStyle() {
        return this.isNarrow ? null : { width: `${this.effectivePanelWidth}px` };
      },
      isAttached() {
        return !!this.documentId;
      },
      tabs() {
        return TABS.filter((t) => this.isAttached || !t.attachedOnly);
      },
      currentTab() {
        return this.tabs.find((t) => t.key === this.tabKey) || this.tabs[0];
      },
      canManage() {
        return this.isAttached && !this.readonly && $has(permissions.Documents.Update);
      },
      canUpload() {
        return this.isAttached && !this.readonly && $has(permissions.Documents.Upload);
      },
      canEdit() {
        return this.canManage && !!this.document;
      },
      isOldVersion() {
        return !!this.selectedVersion && !this.selectedVersion.isCurrent;
      },
      /** What the viewer shows: the document (current file), or one of its older versions. */
      viewerDocument() {
        if (!this.document || this.pendingRevisionId) return null;
        if (!this.isOldVersion) return this.document;
        const version = this.selectedVersion;
        return {
          ...this.document,
          fileName: version.fileName,
          size: version.size,
          contentType: version.contentType,
          revisionNumber: version.revisionNumber,
          revisionDescription: version.revisionDescription,
          createdBy: version.createdByName,
          createDate: version.createDate,
          versionNumber: version.versionNumber,
          // An older version is read from its own file URL, with the API stream as the fallback
          // (documentProxyUtils.fetchVersionAsObjectUrl). `storageReference` stays the current file's.
          viewRevisionId: version.id,
          viewStorageReference: version.storageReference || null,
          storageReference: null,
        };
      },
      shown() {
        return this.viewerDocument || this.document || {};
      },
      shownVersionNumber() {
        return this.isOldVersion ? this.selectedVersion.versionNumber : this.document?.currentVersionNumber;
      },
      shownRevisionId() {
        if (this.selectedVersion) return this.selectedVersion.id;
        return this.document?.currentRevisionId || null;
      },
      extension() {
        return documentExtension(this.shown);
      },
      title() {
        const doc = this.document;
        return doc?.documentNumber || doc?.documentTitle || doc?.fileName || 'Document';
      },
      subtitle() {
        const doc = this.document;
        if (!doc) return '';
        const parts = [];
        if (doc.documentTitle && doc.documentTitle !== this.title) parts.push(doc.documentTitle);
        if (this.shown.fileName && this.shown.fileName !== this.title) parts.push(this.shown.fileName);
        return parts.join(' · ');
      },
      documentTypeName() {
        const id = this.document?.documentTypeId;
        const type = id ? (this.$store.getters.GET_DOCUMENT_TYPES || []).find((t) => t.id == id) : null;
        return type?.name || this.document?.documentType || '';
      },
    },
    mounted() {
      (window.document.querySelector('[data-app]') || window.document.body).appendChild(this.$el);
      window.addEventListener('resize', this.onViewportResize);
      EventBus.$on(DOCUMENT_EVENTS.DOCUMENT_CHANGED, this.onExternalDocumentChange);
    },
    beforeDestroy() {
      EventBus.$off(DOCUMENT_EVENTS.DOCUMENT_CHANGED, this.onExternalDocumentChange);
      window.removeEventListener('resize', this.onViewportResize);
      this.stopInteraction();
      // We moved the element out of the parent's DOM, so the parent will not remove it for us.
      this.$el.parentNode?.removeChild(this.$el);
    },
    methods: {
      open({ documentId = null, document = null, revisionId = null, tab = null, readonly = false } = {}) {
        const id = documentId || document?.id || document?.documentId || null;
        this.documentId = id;
        // A version the caller was showing is not the document; start from the document itself.
        this.document = document ? { ...document, viewRevisionId: undefined, id: id || document.id } : null;
        this.readonly = readonly;
        this.loadError = '';
        this.selectedVersion = null;
        this.pendingRevisionId = id && revisionId ? revisionId : null;
        this.versionNumbers = {};
        this.versionTotal = 0;
        // Collapsed unless the caller asked for a section.
        this.tabKey = this.tabs.some((t) => t.key === tab) ? tab : 'details';
        this.panelOpen = !!tab;
        if (!this.active) this.placeWindow();
        this.minimized = false;
        this.active = true;
        this.$nextTick(() => this.$refs.window?.focus({ preventScroll: true }));
        if (id) this.loadDocument();
      },
      close() {
        this.stopInteraction();
        this.active = false;
        this.minimized = false;
        // Unmounting the viewer frees the file and pdf.js memory.
        this.document = null;
        this.selectedVersion = null;
      },
      onEscape(event) {
        if (event.defaultPrevented || this.minimized) return;
        this.close();
      },
      loadDocument() {
        const id = this.documentId;
        this.loadError = '';
        return documentsService
          .getById(id)
          .then((res) => {
            if (id !== this.documentId) return;
            this.document = res.data;
          })
          .catch((err) => {
            if (id !== this.documentId) return;
            if (this.document) {
              warn('[DocumentWorkspace] Could not refresh the document; showing what was passed in', err);
              return;
            }
            const status = err?.response?.status;
            this.loadError =
              status === 404 || status === 400
                ? 'This document was not found. It may have been deleted.'
                : status === 403
                  ? 'You do not have access to this document.'
                  : 'The document could not be loaded.';
          });
      },
      onVersionsLoaded({ versions, versionNumbers }) {
        this.versionNumbers = versionNumbers;
        this.versionTotal = this.$refs.versions?.total || versions.length;
        if (this.pendingRevisionId) {
          const wanted = versions.find((v) => v.id === this.pendingRevisionId);
          // Not on the first page: show it anyway; the viewer only needs its id and file name.
          this.selectedVersion =
            wanted || { id: this.pendingRevisionId, isCurrent: false, fileName: this.document?.fileName };
          this.pendingRevisionId = null;
          return;
        }
        // Keep the selection in step with the fresh list (after a pin, `isCurrent` moves).
        if (this.selectedVersion) {
          this.selectedVersion = versions.find((v) => v.id === this.selectedVersion.id) || this.selectedVersion;
        }
      },
      selectVersion(version) {
        this.selectedVersion = version.isCurrent ? null : version;
      },
      showCurrent() {
        this.selectedVersion = null;
      },
      setSelectedAsCurrent() {
        if (this.selectedVersion) this.$refs.versions?.setAsCurrent(this.selectedVersion);
      },
      onDocumentChanged(document) {
        this.document = document;
        this.$refs.history?.reset();
        this.$emit('changed', document);
        EventBus.$emit(DOCUMENT_EVENTS.DOCUMENT_CHANGED, document);
      },
      /**
       * A change made outside this window, such as a new version finishing in the upload tray.
       * Our own changes come back here too; they are the object we already hold, so skip them.
       */
      onExternalDocumentChange(document) {
        if (!this.active || !document || document === this.document) return;
        if ((document.id || document.documentId) !== this.documentId) return;
        this.document = document;
        this.$refs.versions?.reload();
        this.$refs.history?.reset();
        this.$emit('changed', document);
      },
      edit() {
        this.$refs.updateDocumentModal.open(structuredClone(this.document));
      },
      /**
       * Saves through the service, not the TASK_DOCUMENT store, which would insert this document
       * into the task list even when it is not a task document. Listeners patch their own rows
       * from the `changed` / DOCUMENT_CHANGED events.
       */
      onUpdateCompleted(edited) {
        documentsService
          .update(edited)
          .then((res) => {
            if (!res?.data) return;
            this.onDocumentChanged(res.data);
            // The revision label is shown on the current version's row.
            this.$refs.versions?.reload();
          })
          .catch($handleError);
      },

      // Side panel ----------------------------------------------------------------------------

      togglePanel() {
        this.panelOpen = !this.panelOpen;
      },
      /** A rail button opens its section, or collapses the panel when that section is already open. */
      toggleTab(key) {
        if (this.panelOpen && this.tabKey === key) {
          this.panelOpen = false;
          return;
        }
        this.tabKey = key;
        this.panelOpen = true;
      },
      setPanelWidth(width) {
        const container = this.$refs.body?.clientWidth || window.innerWidth;
        this.panelWidth = resizePanelWidth(width, 0, container);
      },
      startPanelResize(event) {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.beginInteraction(event, { type: 'panel', startWidth: this.effectivePanelWidth });
      },
      resizePanelWithKeyboard(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const step = event.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP;
        this.setPanelWidth(this.effectivePanelWidth + (event.key === 'ArrowLeft' ? step : -step));
        writeStored(PANEL_WIDTH_STORAGE_KEY, this.panelWidth);
      },

      // Window position and size -------------------------------------------------------------

      placeWindow() {
        const bounds = viewportBounds();
        const saved = readStored(WINDOW_STORAGE_KEY);
        this.rect = isValidRect(saved)
          ? clampRect(saved, bounds, MIN_WINDOW)
          : centeredRect(
              { width: Math.min(1280, bounds.width * 0.72), height: Math.min(880, bounds.height * 0.85) },
              bounds,
              MIN_WINDOW,
            );
      },
      onViewportResize() {
        if (this.active && this.rect) this.rect = clampRect(this.rect, viewportBounds(), MIN_WINDOW);
        if (this.miniPosition) this.miniPosition = this.clampMini(this.miniPosition);
      },

      // Minimized bar -------------------------------------------------------------------------

      minimize() {
        this.stopInteraction();
        this.miniPosition = this.clampMini(readStored(MINI_STORAGE_KEY) || this.defaultMiniPosition());
        this.minimized = true;
      },
      restore() {
        this.stopInteraction();
        this.minimized = false;
        this.$nextTick(() => this.$refs.window?.focus({ preventScroll: true }));
      },
      /** Bottom left, clear of the viewport edge. */
      defaultMiniPosition(width = MINI_SIZE.width) {
        return this.clampMini({ left: MINI_BOTTOM_GAP, top: window.innerHeight - MINI_SIZE.height - MINI_BOTTOM_GAP }, width);
      },
      clampMini(position, width = Math.min(MINI_SIZE.width, window.innerWidth - 16)) {
        const rect = clampRect(
          { left: Number(position?.left) || 0, top: Number(position?.top) || 0, width, height: MINI_SIZE.height },
          viewportBounds(),
          { width, height: MINI_SIZE.height },
        );
        return { left: rect.left, top: rect.top };
      },
      startMiniMove(event) {
        if (event.button !== 0 || event.target?.closest?.('button, a, .v-chip')) return;
        event.preventDefault();
        this.beginInteraction(event, { type: 'mini', startMini: { ...this.miniRect } });
      },
      onMiniDoubleClick(event) {
        if (event.target?.closest?.('button')) return;
        this.restore();
      },
      toggleMaximized() {
        this.maximized = !this.maximized;
      },
      onHeaderDoubleClick(event) {
        if (this.isNarrow || event.target?.closest?.('button, a, input, .v-chip')) return;
        this.toggleMaximized();
      },
      startMove(event) {
        if (!this.movable || event.button !== 0) return;
        if (event.target?.closest?.('button, a, input, textarea, select, .v-chip')) return;
        event.preventDefault();
        this.beginInteraction(event, { type: 'move' });
      },
      startResize(event, direction) {
        if (!this.resizable || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.beginInteraction(event, { type: 'resize', direction });
      },
      beginInteraction(event, details) {
        this.interaction = {
          ...details,
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startRect: { ...this.rect },
        };
        window.addEventListener('pointermove', this.onPointerMove, { passive: false });
        window.addEventListener('pointerup', this.endInteraction);
        window.addEventListener('pointercancel', this.endInteraction);
      },
      onPointerMove(event) {
        const action = this.interaction;
        if (!action || event.pointerId !== action.pointerId) return;
        event.preventDefault();
        const dx = event.clientX - action.startX;
        const dy = event.clientY - action.startY;
        if (action.type === 'move') {
          this.rect = moveRect(action.startRect, dx, dy, viewportBounds(), MIN_WINDOW);
        } else if (action.type === 'resize') {
          this.rect = resizeRect(action.startRect, action.direction, dx, dy, viewportBounds(), MIN_WINDOW);
        } else if (action.type === 'mini') {
          const { startMini } = action;
          const moved = moveRect(startMini, dx, dy, viewportBounds(), { width: startMini.width, height: startMini.height });
          this.miniPosition = { left: moved.left, top: moved.top };
        } else if (action.type === 'panel') {
          const container = this.$refs.body?.clientWidth || window.innerWidth;
          this.panelWidth = resizePanelWidth(action.startWidth, dx, container);
        }
      },
      endInteraction(event) {
        const action = this.interaction;
        if (!action || (event && event.pointerId !== action.pointerId)) return;
        if (action.type === 'panel') writeStored(PANEL_WIDTH_STORAGE_KEY, this.panelWidth);
        else if (action.type === 'mini') writeStored(MINI_STORAGE_KEY, this.miniPosition);
        else writeStored(WINDOW_STORAGE_KEY, this.rect);
        this.stopInteraction();
      },
      stopInteraction() {
        this.interaction = null;
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.endInteraction);
        window.removeEventListener('pointercancel', this.endInteraction);
      },
      resizeWithKeyboard(event) {
        const steps = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const step = steps[event.key];
        if (!step) return;
        event.preventDefault();
        const size = event.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP;
        this.rect = resizeRect(this.rect, 'se', step[0] * size, step[1] * size, viewportBounds(), MIN_WINDOW);
        writeStored(WINDOW_STORAGE_KEY, this.rect);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .dw {
    position: fixed;
    /* Above the app chrome, below Vuetify dialogs (confirmations, edit), which start at 200. */
    z-index: 190;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    outline: none;
    border-radius: var(--ds-radius-md, 8px);
    border: 1px solid rgba(128, 128, 128, 0.3);
    background: var(--v-background-base, #ffffff);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28), 0 2px 8px rgba(0, 0, 0, 0.16);

    .theme--dark & {
      background: #1e1e1e;
    }

    &--maximized {
      inset: 8px;
    }

    &--narrow {
      inset: 0;
      border-radius: 0;
      border: none;
    }

    &--minimized {
      border-radius: var(--ds-radius-full, 9999px);
    }

    &--interacting {
      user-select: none;

      ::v-deep iframe {
        pointer-events: none;
      }
    }
  }

  .dw__mini {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 100%;
    padding: 0 8px 0 14px;
    cursor: grab;
    touch-action: none;
    user-select: none;

    .dw--interacting & {
      cursor: grabbing;
    }
  }

  .dw__mini-icon {
    flex: none;
    font-size: 20px;
  }

  .dw__mini-title {
    flex: 1;
    min-width: 0;
    font-size: var(--ds-font-body-sm, 13px);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dw__mini-chip {
    flex: none;
  }

  .dw__header {
    flex: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    padding: 8px 8px 8px 14px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.25);
    touch-action: none;

    &--movable {
      cursor: grab;
    }

    .dw--interacting &--movable {
      cursor: grabbing;
    }
  }

  .dw__file-icon {
    flex: none;
    font-size: 26px;
  }

  .dw__titles {
    min-width: 0;
    flex: 0 1 auto;
  }

  .dw__title {
    margin: 0;
    font-family: var(--ds-font-family-heading, inherit);
    font-size: var(--ds-font-subtitle, 16px);
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 40vw;
  }

  .dw__subtitle {
    font-size: var(--ds-font-caption, 12px);
    opacity: 0.7;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 40vw;
  }

  .dw__chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  .dw__actions {
    display: flex;
    align-items: center;
    gap: 2px;
    cursor: default;
  }

  .dw__banner {
    flex: none;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 6px 14px;
    font-size: var(--ds-font-body-sm, 13px);
    background: rgba(217, 119, 6, 0.1);
    border-bottom: 1px solid rgba(217, 119, 6, 0.35);
  }

  .dw__body {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
  }

  .dw__viewer {
    flex: 1;
    min-width: 0;
    display: flex;
  }

  .dw__state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 24px;
    text-align: center;
  }

  .dw__state-icon {
    opacity: 0.55;
  }

  /* Side panel: [resize handle][content][rail]. Collapsed, only the rail shows. */
  .dw__panel {
    position: relative;
    flex: none;
    display: flex;
    min-height: 0;
    border-left: 1px solid rgba(128, 128, 128, 0.25);
    background: var(--v-background-base, #ffffff);

    .theme--dark & {
      background: #1e1e1e;
    }
  }

  .dw__panel-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    border-right: 1px solid rgba(128, 128, 128, 0.2);

    .dw--narrow & {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 100%;
      z-index: 5;
      width: min(calc(100vw - 44px), 420px);
      background: inherit;
      box-shadow: -4px 0 16px rgba(0, 0, 0, 0.2);
    }
  }

  .dw__panel-header {
    flex: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    padding: 0 6px 0 14px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.2);
  }

  .dw__panel-title {
    margin: 0;
    font-size: var(--ds-font-body-sm, 13px);
    font-weight: 600;
  }

  .dw__tab-count {
    margin-left: 6px;
    padding: 0 6px;
    border-radius: var(--ds-radius-full, 9999px);
    font-size: var(--ds-font-micro, 10px);
    font-weight: 500;
    background: rgba(128, 128, 128, 0.18);
  }

  .dw__panel-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .dw__panel-resize {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -4px;
    z-index: 4;
    width: 8px;
    cursor: col-resize;
    touch-action: none;

    &:hover,
    &:focus-visible {
      background: rgba(25, 118, 210, 0.25);
      background: color-mix(in srgb, var(--v-primary-base) 30%, transparent);
      outline: none;
    }
  }

  .dw__rail {
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 44px;
    padding: 8px 0;
  }

  .dw__rail-button {
    position: relative;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: var(--ds-radius-md, 8px);
    color: inherit;
    opacity: 0.75;

    &:hover {
      opacity: 1;
      background: rgba(128, 128, 128, 0.14);
    }

    &--active {
      opacity: 1;
      color: var(--v-primary-base);
      background: rgba(128, 128, 128, 0.14);
      box-shadow: inset 2px 0 0 var(--v-primary-base);
    }

    &:focus-visible {
      outline: 2px solid var(--v-primary-base);
      outline-offset: 1px;
    }
  }

  .dw__rail-badge {
    position: absolute;
    top: 0;
    right: -2px;
    min-width: 16px;
    padding: 0 4px;
    border-radius: var(--ds-radius-full, 9999px);
    font-size: 9px;
    line-height: 16px;
    color: #fff;
    background: var(--v-primary-base);
  }

  /* Window resize zones: thin strips on the edges, squares in the corners. */
  .dw__resize {
    position: absolute;
    z-index: 6;
    touch-action: none;

    &--n,
    &--s {
      left: 10px;
      right: 10px;
      height: 6px;
      cursor: ns-resize;
    }

    &--e,
    &--w {
      top: 10px;
      bottom: 10px;
      width: 6px;
      cursor: ew-resize;
    }

    &--n {
      top: 0;
    }

    &--s {
      bottom: 0;
    }

    &--e {
      right: 0;
    }

    &--w {
      left: 0;
    }

    &--ne,
    &--nw,
    &--se,
    &--sw {
      width: 12px;
      height: 12px;
    }

    &--ne {
      top: 0;
      right: 0;
      cursor: nesw-resize;
    }

    &--nw {
      top: 0;
      left: 0;
      cursor: nwse-resize;
    }

    &--se {
      bottom: 0;
      right: 0;
      cursor: nwse-resize;
    }

    &--sw {
      bottom: 0;
      left: 0;
      cursor: nesw-resize;
    }
  }

  .dw__resize-grip {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 7;
    width: 18px;
    height: 18px;
    padding: 0;
    cursor: nwse-resize;
    touch-action: none;
    background: linear-gradient(
      135deg,
      transparent 0 55%,
      rgba(128, 128, 128, 0.55) 55% 62%,
      transparent 62% 72%,
      rgba(128, 128, 128, 0.55) 72% 79%,
      transparent 79%
    );

    &:focus-visible {
      outline: 2px solid var(--v-primary-base);
      outline-offset: -2px;
    }
  }
</style>
